import React, { useEffect, useState } from "react";
import data from "../data/photographer.json";
import { Link } from "react-router-dom"; // Make sure to place this in /src/assets or /public if needed

const Photographer = () => {
  const {
    name,
    bio,
    profilePic,
    instagram,
    portfolio,
    contact,
    gallery,
    projects,
  } = data;

  const [flashVisible, setFlashVisible] = useState(false);

  useEffect(() => {
    setFlashVisible(true);
    const timer = setTimeout(() => setFlashVisible(false), 600); // flash duration
    return () => clearTimeout(timer);
  }, []);

  const handleImageClick = () => {
    setFlashVisible(true);
    setTimeout(() => setFlashVisible(false), 600);
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-700 via-pink-500 to-blue-500 bg-size-200 animate-gradient-x overflow-x-hidden relative">
      <div className="absolute top-6 left-6 z-20 flex items-center gap-3">
        <Link to="/templates" className="flex items-center space-x-3">
        <img src="/logo.png" alt="Project Port Logo" className="h-12 w-12 drop-shadow-lg" />
        <span className="text-xl font-bold text-black">Project Port</span>
        </Link>
      </div>
      {/* Flash Overlay */}
      {flashVisible && (
        <div className="fixed top-0 left-0 w-full h-full bg-white opacity-80 z-50 animate-flash pointer-events-none"></div>
      )}

      <div className="relative bg-black/80 text-white font-sans overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-80 h-80 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-0 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-10 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-4000"></div>

        {/* Profile Section */}
        <section className="relative z-10 flex flex-col items-center justify-center py-16 text-center px-4 animate-fadeIn">
          <img
            src={profilePic}
            alt="Profile"
            className="w-40 h-40 md:w-56 md:h-56 rounded-full border-4 border-purple-500 shadow-lg mb-4 transition duration-500 hover:scale-105"
          />
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-wide mb-2 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
            {name}
          </h1>
          <p className="text-gray-300 max-w-2xl text-lg leading-relaxed">{bio}</p>
          <div className="flex gap-6 mt-6">
            <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white transition underline">
              Instagram
            </a>
            <a href={portfolio} target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white transition underline">
              Portfolio
            </a>
            <a href={contact} target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white transition underline">
              Contact
            </a>
          </div>
        </section>

        {/* Project Showcase */}
        <section className="relative z-10 py-12 px-6 max-w-6xl mx-auto animate-slideUp">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Featured Projects</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {projects.map((proj, i) => (
              <div
                key={i}
                className="bg-[#1e1e1e]/80 backdrop-blur-md border border-purple-400 rounded-xl p-6 shadow-xl hover:scale-105 transition duration-500"
              >
                <img
                  src={proj.image}
                  alt={proj.name}
                  className="rounded-lg h-40 w-full object-cover mb-4"
                />
                <h3 className="text-xl font-semibold mb-2">{proj.name}</h3>
                <p className="text-gray-400 text-sm mb-3">{proj.desc}</p>
                <a
                  href={proj.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-300 hover:text-white underline"
                >
                  View Project →
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Gallery Section */}
        <section className="relative z-10 py-16 px-6 animate-slideUp">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Art & Photography Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Gallery ${i + 1}`}
                onClick={handleImageClick}
                className="rounded-lg object-cover w-full h-48 hover:scale-105 transition-transform duration-300 shadow-md cursor-pointer"
              />
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 text-center py-6 text-sm text-gray-400 border-t border-purple-600">
          © {new Date().getFullYear()} {name} • Photographer & Artist
        </footer>
      </div>
    </div>
  );
};

export default Photographer;
